/**
 * axiosAuth.ts
 *
 * Axios інстанс для всіх захищених запитів.
 * Замінює прямий axios.post(..., { headers: { Authorization: `Bearer ${token}` } })
 * у будь-якому місці проекту.
 *
 * Використання:
 *   import axiosAuth from '../services/axiosAuth';
 *   const response = await axiosAuth.get('/api/identify');
 *   const response = await axiosAuth.post('/api/quicktips/reserve', data);
 *   // Bearer підставляється автоматично
 *
 * Request interceptor:
 *   Перевіряє isTokenValid() перед запитом.
 *   Якщо токен протух → робить POST /api/refresh → оновлює tokenService → продовжує запит.
 *   Це страховка на випадок якщо setTimeout в scheduleRefresh не спрацював
 *   (наприклад вкладка була неактивна).
 *
 * Response interceptor:
 *   Якщо сервер повернув 401 (race condition між перевіркою і відповіддю) →
 *   refresh → retry. Паралельні запити з 401 стають у чергу waitQueue
 *   і виконуються всі після одного refresh.
 */

import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios';
import { getAccessToken, getRefreshToken, setTokens, clearTokens, isTokenValid } from './tokenService';

// Окремий plain інстанс для refresh — без interceptorів (уникаємо нескінченного циклу)
const axiosPlain = axios.create();

const doRefresh = async (): Promise<void> => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) throw new Error('No refresh token');

    const response = await axiosPlain.post<{
        access_token:            string;
        refresh_token:           string;
        access_token_expires_in: number;
    }>('/api/refresh', { refresh_token: refreshToken });

    // Оновлюємо tokenService одразу — не чекаємо Redux
    setTokens(response.data);
};

// Черга паралельних запитів що очікують на refresh
let isRefreshing = false;
let waitQueue: Array<(token: string) => void> = [];

const processQueue = (token: string) => {
    waitQueue.forEach(cb => cb(token));
    waitQueue = [];
};

// ── Захищений інстанс ─────────────────────────────────────────────────────────
const axiosAuth: AxiosInstance = axios.create();

// Request interceptor
axiosAuth.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
        // Страховка: токен протух → refresh перед запитом
        if (!isTokenValid() && getRefreshToken()) {
            try {
                await doRefresh();
            } catch {
                clearTokens();
            }
        }

        const token = getAccessToken();
        console.log('[axiosAuth] Authorization header:', token ? `Bearer ${token.slice(0, 20)}...` : 'MISSING');
        if (token) {
            config.headers = config.headers ?? {};
            config.headers['Authorization'] = `Bearer ${token}`;
        }

        return config;
    },
    error => Promise.reject(error),
);

// Response interceptor
axiosAuth.interceptors.response.use(
    response => response,
    async (error: AxiosError) => {
        const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        if (error.response?.status === 401 && !original._retry) {
            if (isRefreshing) {
                // Інший запит вже оновлює — стаємо в чергу
                return new Promise(resolve => {
                    waitQueue.push((token: string) => {
                        original.headers['Authorization'] = `Bearer ${token}`;
                        resolve(axiosAuth(original));
                    });
                });
            }

            original._retry = true;
            isRefreshing    = true;

            try {
                await doRefresh();
                const newToken = getAccessToken()!;
                isRefreshing = false;
                processQueue(newToken);
                original.headers['Authorization'] = `Bearer ${newToken}`;
                return axiosAuth(original);
            } catch {
                isRefreshing = false;
                waitQueue    = [];
                clearTokens();
                return Promise.reject(error);
            }
        }

        return Promise.reject(error);
    },
);

export default axiosAuth;